import { createReactive } from "@supergrain/kernel"
import { getAttendedEditorId, registerAttentionRoot } from "./editor-attention"

/**
 * Which transient surfaces may show, decided in one place.
 *
 * The model: boundaries `report()` observations (inputs); inputs derive ACTIVITIES
 * (what the user is doing — any subset can be active at once); SURFACES declare
 * `showsDuring` (the activity they are the face of) and `hiddenWhen` (the activities
 * that hide them). A surface is visible exactly when its activity is active (or it
 * has none) and no `hiddenWhen` entry names an active activity — there is no other
 * path to visibility, and no component keeps a private visibility predicate.
 *
 * Extending: a new activity is one `activeWhen` line; a new surface is one block —
 * include EDITOR_SUSPENDED unless it must survive drags/overlays/attention loss (the
 * unit suite enforces this). A visibility bug is a missing input, a stale boundary
 * (an input must be re-reported on every event that can change it), or a wrong entry
 * here — never an if-statement in a component. Debugging: inspect `arbiter.reported`
 * and `arbiter.visible`, or probe hypotheticals with the pure `resolveVisibility`.
 */

/** Observations reported at the boundaries. Every input is always present; these
 *  initial values are also the shape — `SurfaceInputs` is derived from this object. */
const SURFACE_INPUTS = {
  isDragging: false,
  /** A react-aria overlay (card menu, link dialog) is open; react-aria owns its state. */
  isOverlayOpen: false,
  isSelectionEligible: false,
  /** Escape dismissed the toolbar; cleared when the selection changes (its boundary). */
  isToolbarDismissed: false,
  isLinkHovered: false,
}

export type SurfaceInputs = typeof SURFACE_INPUTS

type EditorCtx = { editorId: string; attendedEditorId: string | null }

// ─── Activities: what is the user doing right now? `activeWhen` is all an activity has. ───

const ACTIVITIES = {
  selectingText: (i: SurfaceInputs) => i.isSelectionEligible && !i.isToolbarDismissed,
  hoveringLink: (i: SurfaceInputs) => i.isLinkHovered,
  draggingBlock: (i: SurfaceInputs) => i.isDragging,
  usingOverlay: (i: SurfaceInputs) => i.isOverlayOpen,
  inAnotherEditor: (_i: SurfaceInputs, ctx: EditorCtx) =>
    ctx.attendedEditorId !== null && ctx.attendedEditorId !== ctx.editorId,
}

export type Activity = keyof typeof ACTIVITIES

/** The activities during which the editor is effectively suspended. Every surface
 *  hides during these unless it has a written (and tested) reason not to. */
export const EDITOR_SUSPENDED: readonly Activity[] = [
  "draggingBlock",
  "usingOverlay",
  "inAnotherEditor",
]

/** Suspended like the rest, but revealable while attention is in another editor: surfaces
 *  that only appear on live hover/focus of their own block can't be stale. */
const SUSPENDED_EXCEPT_OWN_HOVER: readonly Activity[] = EDITOR_SUSPENDED.filter(
  (activity) => activity !== "inAnotherEditor",
)

// ─── Surfaces: shown by their activity, hidden by others (a veto always wins). ───
// Surfaces speak only activity-language — never inputs. The layering is strict:
// inputs → activities → surfaces.

type SurfaceDef = {
  /** The activity this surface is the face of — it shows only while that activity is
   *  active. Omitted = always wants to show (chrome whose real trigger —
   *  hover/focus-within — is tracked locally by useHoverChrome / CSS hover geometry). */
  showsDuring?: Activity
  hiddenWhen: readonly Activity[]
}

// `satisfies` (not an annotation): validates each entry as a SurfaceDef while the
// object keeps its literal keys, so `Surface` below is derived from them — the
// surfaces are listed exactly once.
const SURFACES = {
  selectionToolbar: {
    showsDuring: "selectingText",
    hiddenWhen: [...EDITOR_SUSPENDED],
  },
  linkHoverMenu: {
    showsDuring: "hoveringLink",
    hiddenWhen: [...EDITOR_SUSPENDED, "selectingText"],
  },
  /** The per-card Add + Card-menu buttons. They survive a selection (acting on the card
   *  while text is selected is fine) and survive attention in another editor: they only
   *  reveal on live hover/focus of their own card, so they can't be stale, and typing in
   *  one lesson must not make a hovered card in another lesson unresponsive. */
  cardButtons: { hiddenWhen: SUSPENDED_EXCEPT_OWN_HOVER },
  /** Block/card drag pads (painted via --chrome-reveal-opacity + CSS hover geometry).
   *  Same policy as cardButtons: the move handle must come up WITH the +/⋯ buttons on a
   *  card hovered in a non-attended lesson. Its opacity gates the card handle too, so a
   *  mismatch here would show the buttons but not the handle. */
  dragHandle: { hiddenWhen: SUSPENDED_EXCEPT_OWN_HOVER },
  /** A standard widget's hover actions — hidden while selecting, unlike cardButtons:
   *  they overlay the content the selection lives in. */
  standardActions: { hiddenWhen: [...EDITOR_SUSPENDED, "selectingText"] },
} satisfies Record<string, SurfaceDef>

export type Surface = keyof typeof SURFACES

// ─── Resolution ───

/** The whole calculation, pure: inputs + context in, one verdict per surface out.
 *  The return type keeps this honest: a new surface won't compile until it has a line. */
export function resolveVisibility(inputs: SurfaceInputs, ctx: EditorCtx): Record<Surface, boolean> {
  const isActive = (activity: Activity) => ACTIVITIES[activity](inputs, ctx)
  const shows = ({ showsDuring, hiddenWhen }: SurfaceDef) =>
    (showsDuring === undefined || isActive(showsDuring)) && !hiddenWhen.some(isActive)
  return {
    selectionToolbar: shows(SURFACES.selectionToolbar),
    linkHoverMenu: shows(SURFACES.linkHoverMenu),
    cardButtons: shows(SURFACES.cardButtons),
    dragHandle: shows(SURFACES.dragHandle),
    standardActions: shows(SURFACES.standardActions),
  }
}

let arbiterCount = 0

/**
 * One per editor. Boundaries `report()` observations in; components listen to their
 * own field of `visible` out (a reactive object — read it in a `tracked` component's
 * render or inside a kernel `effect`; unchanged verdicts propagate nothing). All
 * resolving happens here, in one explicit pass per event.
 */
export class SurfaceArbiter {
  /** The verdicts. `arbiter.visible.linkHoverMenu` is THE answer for that surface. */
  readonly visible: Record<Surface, boolean>

  private readonly inputs: SurfaceInputs = { ...SURFACE_INPUTS }

  constructor(readonly editorId: string = `editor-${++arbiterCount}`) {
    this.visible = createReactive(resolveVisibility(this.inputs, this.ctx()))
  }

  /** Boundaries call this from their event handlers. Atomic: one resolve per call. */
  report(change: Partial<SurfaceInputs>): void {
    Object.assign(this.inputs, change)
    this.resolve()
  }

  /** A boundary may read back what it reported (never another boundary's inputs). */
  get reported(): Readonly<SurfaceInputs> {
    return this.inputs
  }

  /**
   * Register a DOM root (the editor container, its floating portal) as this editor's
   * territory for page-level attention tracking. Returns the unregister.
   */
  attachAttentionRoot(element: HTMLElement): () => void {
    return registerAttentionRoot(this.editorId, element, () => this.resolve())
  }

  private ctx(): EditorCtx {
    return { editorId: this.editorId, attendedEditorId: getAttendedEditorId() }
  }

  private resolve(): void {
    // Property-by-property assignment through the reactive proxy: only flipped
    // verdicts propagate.
    Object.assign(this.visible, resolveVisibility(this.inputs, this.ctx()))
  }
}
